<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  type DailyLessonInstructorCommentModalKollegeUserFragment,
  useDailyLessonInstructorCommentModalKollegeUserQuery,
  useUpdateKollegeExpenseRecordDailyLessonInstructorCommentMutation,
} from './DailyLessonInstructorCommentModal.generated'
import DailyLessonExpense from './DailyLessonExpense.vue'
import type { KollegeExpenseRecordDailyLessonInput } from '@/types/schema.generated'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  user: DailyLessonInstructorCommentModalKollegeUserFragment
  nthDay: number
}>()

const emits = defineEmits<{
  close: [{ instructorComment: string | null } | null]
}>()

const { result, error } = useDailyLessonInstructorCommentModalKollegeUserQuery(
  {
    userSubGuid: props.user.id,
    nthDay: props.nthDay,
  },
  {
    fetchPolicy: 'network-only',
  }
)

watch([result, error], ([result, error]) => {
  if (error) {
    throw new Error(error.message)
  }
  if (result == null) {
    throw new Error('result is null')
  }

  // コメントがある場合はセットする
  comment.value = expenseRecordDailyLessonInput.value?.instructorComment ?? ''
})

const expenseRecordDailyLessonInput = computed(() => {
  return result.value?.kollegeUser?.standardCourse?.expenseRecordDailyLesson
    ?.input?.[0]
})

const expenses: {
  title: string
  id: keyof KollegeExpenseRecordDailyLessonInput & `expense${string}`
}[] = [
  { id: 'expenseShokuhi', title: '食費' },
  { id: 'expenseNichiyouhin', title: '日用品費' },
  { id: 'expenseIryohi', title: '医療費' },
  { id: 'expenseHifukuhi', title: '被服費' },
  { id: 'expenseBiyouhi', title: '美容費' },
  { id: 'expenseKousaihi', title: '交際費' },
  { id: 'expenseGorakuhi', title: '娯楽費' },
  { id: 'expenseKodomohi', title: '子供費' },
  { id: 'expenseKoutsuhi', title: '交通費' },
  { id: 'expenseGasolinedai', title: 'ガソリン代' },
  { id: 'expenseZappi', title: '雑費' },
  { id: 'expenseTokubetsuhi', title: '特別費' },
]

const comment = ref(
  expenseRecordDailyLessonInput.value?.instructorComment ?? ''
)

const alert = useAlert()

const { mutate } =
  useUpdateKollegeExpenseRecordDailyLessonInstructorCommentMutation()

const handleCommentSave = async () => {
  const expenseRecordDailyLessonInputID =
    expenseRecordDailyLessonInput.value?.guid
  if (!expenseRecordDailyLessonInputID) {
    return
  }

  const result = await mutate({
    expenseRecordDailyLessonInputID,
    instructorComment: {
      value: comment.value === '' ? null : comment.value,
    },
  })

  if (result?.errors) {
    alert.error(result.errors[0].message)
  }

  if (!result?.data) {
    alert.error('コメントの保存に失敗しました')
    return
  }

  alert.success('コメントを保存しました')
  emits('close', {
    instructorComment:
      result.data.updateKollegeExpenseRecordDailyLessonInstructorComment
        .instructorComment ?? null,
  })
}
</script>

<template>
  <div
    v-if="user.profile && expenseRecordDailyLessonInput"
    :class="$style.main"
  >
    <h2>
      {{ user.profile.familyName
      }}{{ user.profile.givenName }}のデイリーレッスン
      {{ expenseRecordDailyLessonInput?.title }}
    </h2>
    <div :class="$style.expenses">
      <DailyLessonExpense
        v-for="expense in expenses"
        :key="expense.id"
        :expense="expenseRecordDailyLessonInput?.[expense.id]"
        :title="expense.title"
      />
    </div>
    <DolphinTextarea v-model="comment" placeholder="コメントを記載" />
    <div :class="$style.buttons">
      <FlatButton variant="base" @click="() => emits('close', null)">
        キャンセル
      </FlatButton>
      <FlatButton @click="handleCommentSave"> コメントする </FlatButton>
    </div>
  </div>
</template>
<style lang="scss" module>
.main {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.expenses {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(6, auto);
  gap: 8px 40px;
}

.buttons {
  display: flex;
  justify-content: space-between;
}
</style>
